/**
 * Invoice report PDF sections drawn from the analytics block.
 *
 * Flow: summary cards, aging and status side by side, client and month
 * breakdowns, then the invoice rows (drawn by `PdfGenerator`), then detailed
 * metrics as an appendix.
 * @module invoiceAnalyticsPdf
 */

import {
  buildAnalyticsSections,
  formatMoney,
  formatMonth,
  formatNumber,
  formatPercent,
  formatPerMile,
} from "../../components/InvoiceAnalyticsPanel/analyticsFormatters";
import {
  drawKpiCards,
  drawMetricSections,
  drawSectionTitle,
  drawSideBySide,
  drawTableSection,
  ensureSpace,
} from "../../utils/pdfTheme";

const rightAlign = (indexes) =>
  Object.fromEntries(indexes.map((index) => [index, { halign: "right" }]));

/**
 * Draws everything that goes above the invoice rows and the "Invoices" title.
 *
 * @param {jsPDF} pdf
 * @param {object} analytics - `response.analytics`
 * @param {number} y - Start y
 * @param {ReturnType<import("../../utils/pdfTheme").getPdfTheme>} theme
 * @param {object} [options]
 * @param {number} [options.invoiceCount] - Rows in the detail table
 * @returns {number} y for the invoice rows table
 */
export const drawInvoiceSummarySections = (pdf, analytics, y, theme, { invoiceCount } = {}) => {
  const summary = analytics?.summary;
  if (!summary) return y;

  let nextY = drawSectionTitle(pdf, "Summary", y, theme) + 1;
  nextY = drawKpiCards(
    pdf,
    [
      { label: "Gross", value: formatMoney(summary.gross) },
      { label: "Paid", value: formatMoney(summary.paid) },
      { label: "Outstanding", value: formatMoney(summary.outstanding) },
      {
        label: "Overdue",
        value: formatMoney(summary.overdue),
        tone: summary.overdue > 0 ? "negative" : undefined,
      },
      { label: "Collected", value: formatPercent(summary.collectionRate) },
      { label: "Revenue / mile", value: formatPerMile(summary.operations?.revenuePerMile) },
    ],
    nextY,
    theme
  );

  nextY = drawSideBySide(
    pdf,
    [
      {
        title: "Outstanding aging",
        head: ["Bucket", "Invoices", "Amount"],
        body: (analytics.aging || []).map((bucket) => [
          bucket.label,
          formatNumber(bucket.count),
          formatMoney(bucket.amount),
        ]),
        options: { columnStyles: rightAlign([1, 2]) },
      },
      {
        title: "By status",
        head: ["Status", "Invoices", "Amount"],
        body: (analytics.byStatus || []).map((row) => [
          row.status,
          formatNumber(row.count),
          formatMoney(row.amount),
        ]),
        options: { columnStyles: rightAlign([1, 2]) },
      },
    ],
    nextY,
    theme
  );

  if ((analytics.byClient || []).length > 1) {
    nextY = drawTableSection(pdf, {
      title: "By client",
      head: ["Client", "Invoices", "Gross", "Paid", "Outstanding", "Overdue", "Fuel % base", "Rev / mile"],
      body: analytics.byClient.map((row) => [
        row.clientName,
        formatNumber(row.invoiceCount),
        formatMoney(row.gross),
        formatMoney(row.paid),
        formatMoney(row.outstanding),
        formatMoney(row.overdue),
        formatPercent(row.fuelPercentOfBase),
        formatPerMile(row.revenuePerMile),
      ]),
      y: nextY,
      theme,
      options: { columnStyles: rightAlign([1, 2, 3, 4, 5, 6, 7]) },
    });
  }

  nextY = drawTableSection(pdf, {
    title: "By month",
    head: ["Month", "Invoices", "Gross", "Paid", "Outstanding", "Fuel", "Miles"],
    body: (analytics.byMonth || []).map((row) => [
      formatMonth(row.month),
      formatNumber(row.invoiceCount),
      formatMoney(row.gross),
      formatMoney(row.paid),
      formatMoney(row.outstanding),
      formatMoney(row.fuel),
      formatNumber(row.miles),
    ]),
    y: nextY,
    theme,
    options: { columnStyles: rightAlign([1, 2, 3, 4, 5, 6]) },
  });

  nextY = ensureSpace(pdf, nextY, 25);
  const title =
    invoiceCount != null ? `Invoices (${formatNumber(invoiceCount)})` : "Invoices";
  return drawSectionTitle(pdf, title, nextY, theme);
};

/**
 * Detailed metrics appendix after the invoice rows.
 * @param {jsPDF} pdf
 * @param {object} analytics
 * @param {number} y
 * @param {ReturnType<import("../../utils/pdfTheme").getPdfTheme>} theme
 * @returns {number}
 */
export const drawInvoiceMetricSections = (pdf, analytics, y, theme) => {
  const sections = buildAnalyticsSections(analytics);
  if (!sections.length) return y;
  const startY = ensureSpace(pdf, y + 2, 50);
  return drawMetricSections(pdf, sections, startY, theme);
};

export default drawInvoiceSummarySections;
